import React from "react";
import {
  FiWifi, FiRadio, FiCpu, FiBluetooth, FiServer,
  FiCheckCircle, FiAlertTriangle, FiXCircle, FiAlertOctagon, FiHelpCircle
} from "react-icons/fi";
import { MdSettingsInputAntenna } from "react-icons/md";
import { TbPlugConnected } from "react-icons/tb";

// ── Status helpers ────────────────────────────────────────────────────────────
export const STATUS_META = {
  Online:      { color: "#10b981", bg: "#d1fae5", icon: FiCheckCircle,   label: "Online" },
  Warning:     { color: "#f59e0b", bg: "#fef3c7", icon: FiAlertTriangle, label: "Warning" },
  Offline:     { color: "#6b7280", bg: "#f3f4f6", icon: FiXCircle,       label: "Offline" },
  Error:       { color: "#ef4444", bg: "#fee2e2", icon: FiAlertOctagon,  label: "Error" },
  Unverified:  { color: "#8b5cf6", bg: "#ede9fe", icon: FiHelpCircle,    label: "Unverified" },
};

export const statusMeta = (s) => STATUS_META[s] || STATUS_META.Unverified;

// ── Connectivity helpers ──────────────────────────────────────────────────────
export const CONN_META = {
  LoRa:      { color: "#667eea", bg: "#ede9fe", Icon: MdSettingsInputAntenna, label: "LoRa" },
  WiFi:      { color: "#10b981", bg: "#d1fae5", Icon: FiWifi,                 label: "Wi-Fi" },
  Ethernet:  { color: "#3b82f6", bg: "#dbeafe", Icon: FiServer,               label: "Ethernet" },
  Cellular:  { color: "#f59e0b", bg: "#fef3c7", Icon: FiRadio,                label: "Cellular" },
  Bluetooth: { color: "#06b6d4", bg: "#cffafe", Icon: FiRadio,                label: "Bluetooth" },
  Modbus:    { color: "#8b5cf6", bg: "#ede9fe", Icon: FiCpu,                  label: "Modbus" },
};

export const connMeta = (c) => CONN_META[c] || { color: "#6b7280", bg: "#f3f4f6", Icon: FiCpu, label: c };

// ── Role / Type helpers ───────────────────────────────────────────────────────
export const ROLE_META = {
  Gateway:    { color: "#667eea", bg: "#ede9fe", letter: "G" },
  Sensor:     { color: "#10b981", bg: "#d1fae5", letter: "S" },
  Controller: { color: "#f97316", bg: "#ffedd5", letter: "C" },
};

export const roleMeta = (r) => ROLE_META[r] || { color: "#6b7280", bg: "#f3f4f6", letter: "?" };

// ── Health score helpers ──────────────────────────────────────────────────────
export const healthColor = (score) => {
  if (score >= 85) return "#10b981";
  if (score >= 60) return "#f59e0b";
  if (score >= 30) return "#f97316";
  return "#ef4444";
};

export const healthLabel = (score) => {
  if (score >= 85) return "Healthy";
  if (score >= 60) return "Fair";
  if (score >= 30) return "Poor";
  return "Critical";
};

// ── Solution badge colors ─────────────────────────────────────────────────────
export const SOL_COLORS = {
  STP:        { color: "#8b5cf6", bg: "#ede9fe" },
  AQM:        { color: "#10b981", bg: "#d1fae5" },
  Irrigation: { color: "#f59e0b", bg: "#fef3c7" },
  Water:      { color: "#3b82f6", bg: "#dbeafe" },
  Facility:   { color: "#667eea", bg: "#e0e7ff" },
  Fleet:      { color: "#f97316", bg: "#ffedd5" },
  None:       { color: "#6b7280", bg: "#f3f4f6" },
};
export const solColor = (s) => SOL_COLORS[s] || SOL_COLORS.None;

// ── Signal bar helper (RSSI → bars 0-4) ──────────────────────────────────────
export const rssiToBars = (rssi) => {
  if (!rssi) return 0;
  if (rssi >= -65) return 4;
  if (rssi >= -75) return 3;
  if (rssi >= -85) return 2;
  if (rssi >= -95) return 1;
  return 0;
};

// ── HealthRing SVG component ──────────────────────────────────────────────────
export const HealthRing = ({ score, size = 56 }) => {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;
  const col = healthColor(score);
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={5} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={5}
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2 + 5} textAnchor="middle" fontSize={size * 0.25}
        fontWeight="800" fill={col}>{score}</text>
    </svg>
  );
};

// ── Battery bar ───────────────────────────────────────────────────────────────
export const BatteryBar = ({ pct }) => {
  if (pct === null || pct === undefined) return <span style={{ fontSize: 11, color: "#6b7280" }}>Wired</span>;
  const col = pct > 50 ? "#10b981" : pct > 20 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ width: 50, height: 8, background: "#e5e7eb", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: col, borderRadius: 4, transition: "width 0.3s" }} />
      </div>
      <span style={{ fontSize: 11, color: col, fontWeight: 700 }}>{pct}%</span>
    </div>
  );
};

// ── Signal bars ───────────────────────────────────────────────────────────────
export const SignalBars = ({ rssi }) => {
  if (rssi === null || rssi === undefined) return <span style={{ fontSize: 11, color: "#6b7280" }}>—</span>;
  const bars = rssiToBars(rssi);
  const col = bars >= 3 ? "#10b981" : bars === 2 ? "#f59e0b" : "#ef4444";
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
      {[1,2,3,4].map(b => (
        <div key={b} style={{ width: 4, height: 4 + b * 3, background: b <= bars ? col : "#e5e7eb", borderRadius: 1 }} />
      ))}
      <span style={{ fontSize: 10, color: col, marginLeft: 3, fontWeight: 700 }}>{rssi} dBm</span>
    </div>
  );
};

// ── Mini badge ────────────────────────────────────────────────────────────────
export const DvcBadge = ({ text, color, bg, style = {} }) => (
  <span style={{ padding: "2px 8px", borderRadius: 20, background: bg, color, fontSize: 11, fontWeight: 700, ...style }}>{text}</span>
);

// ── Severity badge ────────────────────────────────────────────────────────────
export const SeverityBadge = ({ severity }) => {
  const m = { Critical: { color: "#ef4444", bg: "#fee2e2" }, Warning: { color: "#f59e0b", bg: "#fef3c7" }, Info: { color: "#3b82f6", bg: "#dbeafe" } }[severity] || { color: "#6b7280", bg: "#f3f4f6" };
  return <span style={{ padding: "2px 8px", borderRadius: 20, background: m.bg, color: m.color, fontSize: 11, fontWeight: 700 }}>{severity}</span>;
};
